import * as React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import type { NotifyConfirmOptions } from "@/types/notify.types";
import { useConfirmStore } from "./confirm-store";
import { NOTIFY_VARIANT_TOKENS } from "./tokens";

export function ConfirmHost() {
  const queue = useConfirmStore((s) => s.queue);
  const resolveAndPop = useConfirmStore((s) => s.resolveAndPop);
  const active = queue[0];

  if (!active) return null;
  return <ConfirmDialog key={active.id} item={active} onResolve={resolveAndPop} />;
}

interface ConfirmDialogProps {
  item: { id: number; opts: NotifyConfirmOptions };
  onResolve: (id: number, ok: boolean) => void;
}

function ConfirmDialog({ item, onResolve }: ConfirmDialogProps) {
  const { id, opts } = item;
  const {
    variant,
    title,
    description,
    confirmLabel = "Confirmar",
    cancelLabel = "Cancelar",
    typeToConfirm,
  } = opts;
  const spec = NOTIFY_VARIANT_TOKENS[variant];
  const Icon = spec.icon;

  const [value, setValue] = React.useState("");
  const [armed, setArmed] = React.useState(!typeToConfirm);
  const requiresType = !!typeToConfirm;
  const matches =
    !requiresType || (value.trim() === typeToConfirm.trim() && typeToConfirm.trim() !== "");

  React.useEffect(() => {
    if (!requiresType) return;
    if (!matches) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- desarma quando input perde match — sync legítimo
      setArmed(false);
      return;
    }
    const t = setTimeout(() => setArmed(true), 600);
    return () => clearTimeout(t);
  }, [matches, requiresType]);

  const destructive = variant === "danger";

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onResolve(id, false);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-[oklch(20%_0.01_80_/_0.4)] motion-safe:animate-fade-in" />
        <Dialog.Content
          role="alertdialog"
          aria-modal="true"
          className={cn(
            "fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2",
            "w-[min(480px,calc(100vw-32px))] rounded-[10px] bg-bg-surface shadow-rd-xl",
            "border border-border-subtle p-6 motion-safe:animate-scale-in"
          )}
        >
          <div className="flex items-start gap-3">
            <Icon className={cn("mt-0.5 h-5 w-5 shrink-0", spec.accentClass)} aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-[18px] font-semibold tracking-tight text-text-primary">
                {title}
              </Dialog.Title>
              {description ? (
                <Dialog.Description className="mt-2 text-[13px] text-text-secondary">
                  {description}
                </Dialog.Description>
              ) : null}
            </div>
          </div>
          {requiresType ? (
            <div className="mt-4 space-y-1.5">
              <label
                htmlFor={`notify-confirm-${id}`}
                className="block text-[12px] text-text-secondary"
              >
                Para confirmar, digite{" "}
                <span className="font-mono text-text-primary">{typeToConfirm}</span>
              </label>
              <input
                id={`notify-confirm-${id}`}
                // eslint-disable-next-line jsx-a11y/no-autofocus -- primary input do Dialog destrutivo (Radix recommendation)
                autoFocus
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className={cn(
                  "h-9 w-full rounded-md border border-border-default px-3",
                  "bg-bg-surface text-[13px] text-text-primary",
                  "focus-visible:border-brand-500 focus-visible:shadow-rd-focus focus-visible:outline-none"
                )}
              />
            </div>
          ) : null}
          <div className="mt-5 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => onResolve(id, false)}
              className={cn(
                "h-8 rounded-md px-3 text-[13px] font-medium",
                "text-text-secondary hover:bg-bg-muted-rd",
                "focus-visible:shadow-rd-focus focus-visible:outline-none"
              )}
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              disabled={!armed}
              onClick={() => onResolve(id, true)}
              data-armed={armed}
              className={cn(
                "h-8 rounded-md px-3 text-[13px] font-medium text-white",
                "disabled:cursor-not-allowed disabled:opacity-40",
                "transition-colors motion-safe:[transition-duration:120ms]",
                destructive
                  ? "bg-rd-danger-600 hover:bg-rd-danger-500"
                  : "bg-brand-600 hover:bg-brand-500",
                "focus-visible:shadow-rd-focus focus-visible:outline-none"
              )}
            >
              {confirmLabel}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
